import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  DollarSign,
  Calendar,
  RefreshCw,
  Store,
  MessageCircle,
  Globe,
  ShoppingBag,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const API_URL = BACKEND_URL || '';

const fetchAPI = async (url) => {
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Erro na requisição');
  return response.json();
};

const CashFlowModulesPage = () => {
  const [loading, setLoading] = useState(true);
  const [cashflowData, setCashflowData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedSource, setSelectedSource] = useState('all');
  const [dateRange, setDateRange] = useState('30');

  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - parseInt(dateRange));
    return {
      start_date: start.toISOString(),
      end_date: end.toISOString()
    };
  }, [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange();
      
      const [summaryRes, entriesRes] = await Promise.all([
        fetchAPI(`${API_URL}/api/cashflow/by-source?start_date=${start_date}&end_date=${end_date}`),
        fetchAPI(`${API_URL}/api/cashflow/entries?start_date=${start_date}&end_date=${end_date}&source=${selectedSource}`)
      ]);
      
      setCashflowData(summaryRes);
      setEntries(entriesRes.entries || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, selectedSource]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'pdv': return <Store className="w-5 h-5" />;
      case 'whatsapp': return <MessageCircle className="w-5 h-5" />;
      case 'online': return <Globe className="w-5 h-5" />;
      case 'marketplace': return <ShoppingBag className="w-5 h-5" />;
      default: return <DollarSign className="w-5 h-5" />;
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'pdv': return 'bg-blue-500';
      case 'whatsapp': return 'bg-green-500';
      case 'online': return 'bg-purple-500';
      case 'marketplace': return 'bg-orange-500';
      default: return 'bg-slate-500';
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="cashflow-modules-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Fluxo de Caixa por Módulo</h1>
          <p className="text-slate-500">Controle financeiro separado por origem das vendas</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="15">Últimos 15 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="60">Últimos 60 dias</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards por Módulo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Geral */}
        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Total Geral</p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(cashflowData?.total_geral)}
                </p>
                <p className="text-emerald-200 text-xs mt-1">
                  {cashflowData?.total_entries || 0} entradas
                </p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cards por Source */}
        {cashflowData?.by_source?.map((source) => (
          <Card key={source.source} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">{source.source_name}</p>
                  <p className="text-xl font-bold mt-1 text-emerald-600">
                    {formatCurrency(source.total)}
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    {source.count} {source.count === 1 ? 'entrada' : 'entradas'}
                  </p>
                </div>
                <div className={`${getSourceColor(source.source)} p-3 rounded-lg text-white`}>
                  {getSourceIcon(source.source)}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lista de Entradas */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle>Entradas do Caixa</CardTitle>
            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrar por origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Origens</SelectItem>
                <SelectItem value="pdv">PDV</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="marketplace">Marketplace</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-slate-600 font-medium">Data/Hora</th>
                  <th className="text-left py-3 px-4 text-slate-600 font-medium">Origem</th>
                  <th className="text-left py-3 px-4 text-slate-600 font-medium">Descrição</th>
                  <th className="text-left py-3 px-4 text-slate-600 font-medium">Pagamento</th>
                  <th className="text-right py-3 px-4 text-slate-600 font-medium">Valor</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => (
                  <tr key={entry.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {formatDate(entry.date)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className={`${getSourceColor(entry.source)} p-1.5 rounded text-white`}>
                          {getSourceIcon(entry.source)}
                        </span>
                        <span className="text-sm font-medium">{entry.source_name || entry.source}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm max-w-xs truncate">
                      {entry.description}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="text-xs">
                        {entry.payment_method || '-'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`font-medium flex items-center justify-end gap-1 ${
                        entry.type === 'entrada' ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {entry.type === 'entrada' ? (
                          <ArrowUpRight className="w-4 h-4" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4" />
                        )}
                        {formatCurrency(entry.amount)}
                      </span>
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-slate-500">
                      <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Nenhuma entrada no período selecionado</p>
                      <p className="text-sm mt-1">
                        Finalize pedidos para registrar no fluxo de caixa
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CashFlowModulesPage;
